import React from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/lib/useSeo";
import { ShieldCheck, Ban } from "lucide-react";

const Section = ({ title, children, testid }) => (
  <section className="mb-8" data-testid={testid}>
    <h2 className="text-lg font-semibold text-[#1C4358] mb-2">{title}</h2>
    <div className="text-[15px] leading-relaxed text-[#3a4551] space-y-3">{children}</div>
  </section>
);

export default function PrivacyPolicy() {
  useSeo({
    title: "Privacy Policy | MovingMyMama",
    description:
      "How MovingMyMama handles your family's information. We do not sell your personal information. Read our plain-language privacy policy.",
    canonical: typeof window !== "undefined" ? `${window.location.origin}/privacy-policy` : undefined,
  });

  return (
    <div className="max-w-3xl mx-auto px-5 py-14" data-testid="privacy-policy-page">
      <div className="flex items-center gap-2 mb-2 text-[#9C1E22]">
        <ShieldCheck size={20} />
        <span className="text-sm font-medium uppercase tracking-wide">Your privacy</span>
      </div>
      <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#252221] mb-3">Privacy Policy</h1>
      <p className="text-[15px] text-[#6b7480] mb-6">
        A plain-language explanation of how MovingMyMama handles your family's information. Your trust
        matters to us, so we've tried to keep this simple and honest.
      </p>

      {/* Prominent "do not sell" callout */}
      <div
        data-testid="privacy-do-not-sell-banner"
        className="mb-8 flex items-start gap-3 rounded-xl border border-[#2F7D5A]/25 bg-[#2F7D5A]/8 px-4 py-3.5"
      >
        <Ban size={20} className="text-[#2F7D5A] shrink-0 mt-0.5" />
        <p className="text-[15px] text-[#1C4358] font-medium">
          MovingMyMama does not sell your personal information.
        </p>
      </div>

      <Section title="1. Introduction" testid="privacy-introduction">
        <p>
          MovingMyMama helps families through senior moves and transitions. When you reach out to us, you
          trust us with sensitive details about your family. This policy explains what information we
          collect, how we use it, and the choices you have.
        </p>
      </Section>

      <Section title="2. Information We Collect" testid="privacy-collect">
        <p>
          To help your family plan a senior transition, we may collect the contact and service information
          you share with us — such as your name, phone, email, city, your relationship to your loved one,
          and the concerns or goals you describe.
        </p>
        <p>
          We also collect information you provide through consultations, live chats, intake forms, and any
          documents you choose to upload. We may collect basic, non-identifying analytics about how our
          website is used.
        </p>
      </Section>

      <Section title="3. How We Use Information" testid="privacy-use">
        <p>
          We use the information you provide to respond to your request, help coordinate services,
          communicate with you, and improve our services. We use it only for the services you asked about
          and for legitimate, everyday operational purposes.
        </p>
      </Section>

      <Section title="4. We Do Not Sell Personal Information" testid="privacy-no-sell">
        <p className="font-medium text-[#1C4358]">
          MovingMyMama does not sell your personal information.
        </p>
        <p>
          We do not trade or rent your details to third parties for their own marketing. The information you
          share with us stays focused on helping your family.
        </p>
      </Section>

      <Section title="5. How We Share Information" testid="privacy-share">
        <p>
          We only share information when it's needed to provide services, respond to your request, work with
          trusted service providers, comply with the law, or when you give us permission. You stay in control
          of your family's decisions.
        </p>
      </Section>

      <Section title="6. Service Providers and Partners" testid="privacy-partners">
        <p>
          To help coordinate a move, we may work with trusted service providers and partners — for example,
          real-estate professionals, senior living communities, movers, or care providers. When we connect
          you, we share only the information reasonably needed to help, and you're always free to choose your
          own professionals.
        </p>
      </Section>

      <Section title="7. Cookies / Website Analytics" testid="privacy-cookies">
        <p>
          Our website may use cookies and basic analytics to understand how the site is used and to improve
          your experience. You can control cookies through your browser settings. We do not use this
          information to sell your data.
        </p>
      </Section>

      <Section title="8. Email and Communications" testid="privacy-comms">
        <p>
          We send transactional messages (like appointment and account notices) so we can serve you.
          Marketing emails are sent only where permitted, and you can opt out at any time using the
          unsubscribe link or by contacting us. SMS is not enabled by default and is never sent without your
          explicit opt-in.
        </p>
      </Section>

      <Section title="9. Security" testid="privacy-security">
        <p>
          We take reasonable steps to protect your information. Documents you share are access-controlled, and
          only authorized team members with a legitimate need — and, where applicable, professionals you're
          matched with — can view files relevant to your case. No system can be guaranteed perfectly secure,
          but we work to keep your information safe.
        </p>
      </Section>

      <Section title="10. Data Retention" testid="privacy-retention">
        <p>
          We keep your information only as long as needed to help your family, run our business, and meet
          legal or record-keeping obligations. When information is no longer needed, we take reasonable steps
          to remove or de-identify it.
        </p>
      </Section>

      <Section title="11. Your Choices" testid="privacy-choices">
        <p>
          You can request a copy of your information, ask us to correct it, request deletion, or opt out of
          marketing communications. Submit a request through our{" "}
          <Link to="/privacy/request" className="text-[#9C1E22] underline">Privacy Request form</Link>. We
          verify identity before acting on any request, and deletion requests are reviewed by a person — we
          do not delete records automatically.
        </p>
      </Section>

      <Section title="12. Contact Us" testid="privacy-contact">
        <p>
          Questions about your privacy? We're glad to help. Please{" "}
          <Link to="/contact" className="text-[#9C1E22] underline">contact us</Link> and a member of our team
          will respond.
        </p>
      </Section>

      <Section title="13. Policy Updates" testid="privacy-updates">
        <p>
          We may update this Privacy Policy from time to time. When we make meaningful changes, we'll update
          this page so you always know how your information is handled.
        </p>
      </Section>

      <div className="mt-10 rounded-xl border border-[#B7791F]/25 bg-[#B7791F]/8 px-4 py-3.5" data-testid="privacy-counsel-note">
        <p className="text-[14px] text-[#7a5a12]">
          This Privacy Policy should be reviewed by legal counsel before final public launch.
        </p>
      </div>
    </div>
  );
}
